import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@shared/services/auth.service';
import { NotificationService } from '@shared/services/notificacion/notification.service';
import { ConnectionErrorService } from '@shared/services/connection-error.service';
import { errorIdOf, isStaleVersionError } from '@shared/utils/http/http-error';
import { catchError, from, switchMap, throwError } from 'rxjs';

/** Pasar este token en el contexto de una petición para suprimir el manejo global de errores. */
export const SUPPRESS_HTTP_ERRORS = new HttpContextToken<boolean>(() => false);

/**
 * Estados HTTP que la pantalla muestra por su cuenta (p. ej. el formulario de cita pinta el 400 y
 * el 409 dentro del formulario): el interceptor no repite el aviso para esos estados, pero sigue
 * gestionando el resto (sesión expirada, sin conexión, 500…). Evita el doble aviso.
 */
export const HANDLED_HTTP_STATUSES = new HttpContextToken<readonly number[]>(() => []);

/**
 * Id de correlación del error, para que el usuario lo cite al soporte y se cruce con los logs del
 * backend: el campo `errorId` del cuerpo del 500 y, si falta, la cabecera `X-Error-Id` (solo si el
 * CORS la expone; si no, `headers.get` da `null` y no rompe). `null` si no viene.
 */
export function correlationIdOf(error: HttpErrorResponse): string | null {
  return errorIdOf(error);
}

/**
 * Evita notificaciones idénticas cuando múltiples peticiones concurrentes
 * fallan con el mismo error (ej: varias llamadas 403 al abrir un módulo).
 * La primera se muestra; las duplicadas dentro de la ventana se descartan.
 */
const recentNotifications = new Map<string, number>();
const SUPPRESS_WINDOW_MS = 3000;

function notifyOnce(
  notification: NotificationService,
  method: 'warning' | 'danger' | 'info' | 'success',
  title: string,
  message: string
): void {
  const key = `${method}|${title}|${message}`;
  const now = Date.now();
  const last = recentNotifications.get(key);

  if (last && now - last < SUPPRESS_WINDOW_MS) return;

  recentNotifications.set(key, now);
  notification[method](title, message);

  // Limpiar entradas antiguas para evitar memory leaks
  if (recentNotifications.size > 20) {
    for (const [k, ts] of recentNotifications) {
      if (now - ts >= SUPPRESS_WINDOW_MS) recentNotifications.delete(k);
    }
  }
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const notification = inject(NotificationService);
  const connectionError = inject(ConnectionErrorService);
  const transloco = inject(TranslocoService);

  if (req.context.get(SUPPRESS_HTTP_ERRORS)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Las peticiones con responseType:'blob' (exports, plantillas) entregan el cuerpo
      // del error como Blob en lugar de JSON. Se normaliza antes de procesar.
      if (error.error instanceof Blob && error.error.type?.includes('application/json')) {
        return from(normalizeBlobError(error)).pipe(
          switchMap(normalizedError => {
            if (!req.context.get(HANDLED_HTTP_STATUSES).includes(normalizedError.status)) {
              handleHttpError(normalizedError, authService, notification, router, connectionError, transloco);
            }
            return throwError(() => normalizedError);
          })
        );
      }

      if (!req.context.get(HANDLED_HTTP_STATUSES).includes(error.status)) {
        handleHttpError(error, authService, notification, router, connectionError, transloco);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Convierte un HttpErrorResponse cuyo cuerpo es un Blob JSON en uno con el objeto
 * parseado, de modo que el interceptor pueda leer campos como `operation` y `code`.
 * Si el Blob no contiene JSON válido, retorna el error original sin modificar.
 */
async function normalizeBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
  try {
    const text = await error.error.text();
    const json = JSON.parse(text);
    return new HttpErrorResponse({
      error: json,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url ?? undefined,
    });
  } catch {
    return error;
  }
}

/** Operación de precio sin tarifa: la gestiona la pantalla, sin aviso global. */
function isPriceOperation(error: HttpErrorResponse): boolean {
  return error.error?.code === 'NO_PRICE' || error.error?.operation === 'NO_PRICE';
}

/** Respuesta de regla de negocio/permiso del backend (`ApiResponse` con `operation=BUSINESS_RULE`). */
function isBusinessRule(error: HttpErrorResponse): boolean {
  return error.error?.code === 'BUSINESS_RULE' || error.error?.operation === 'BUSINESS_RULE';
}

/** El cuerpo del error trae un mensaje propio. */
function hasBackendMessage(error: HttpErrorResponse): boolean {
  return typeof error.error === 'object' && error.error !== null && !!error.error.message;
}

function handleHttpError(
  error: HttpErrorResponse,
  authService: AuthService,
  notification: NotificationService,
  router: Router,
  connectionError: ConnectionErrorService,
  transloco: TranslocoService
): void {
  // Títulos/mensajes localizados (el bundle ya está precargado al arranque, así translate() es seguro).
  const t = (key: string) => transloco.translate(`errors.http.${key}`);
  let message = t('unexpected');
  const path = error.error?.path || '';

  // Extraer mensaje si viene del backend
  if (error.error) {
    if (typeof error.error === 'object' && error.error.message) {
      message = error.error.message;
    } else if (typeof error.error === 'string') {
      message = error.error;
    }
  }

  // Endpoints cubiertos por resolvers propios — el componente gestiona toda la UI.
  // Suprimir notificaciones para evitar toasts duplicados.
  const requestUrl = error.url ?? '';
  if (requestUrl.includes('/configuracion/transferir/') && requestUrl.includes('/info')) {
    return;
  }

  switch (error.status) {
    case 0:
      connectionError.notifyOnce(() =>
        notification.danger(t('connectionTitle'), t('connectionMessage'))
      );
      break;

    case 400:
      notifyOnce(notification, 'warning', t('badRequestTitle'), message);
      break;

    case 401:
      // Sesión inválida o expirada (el authInterceptor ya intentó el refresh silencioso).
      if (isPriceOperation(error)) {
        return;
      }
      if (isBusinessRule(error)) {
        notifyOnce(notification, 'warning', t('businessRuleTitle'), message);
        return;
      }
      if (path.includes('/auth/login')) {
        notifyOnce(notification, 'warning', t('authErrorTitle'), message || t('invalidCredentials'));
      } else if (path.includes('/api/user/password')) {
        notifyOnce(notification, 'warning', t('passwordIncorrectTitle'), message);
      } else if (!authService.isSessionHandled()) {
        authService.logout('expirada');
      }
      break;

    case 403:
      // Falta de permiso: la sesión sigue siendo válida, NUNCA se cierra por un 403.
      if (isPriceOperation(error)) {
        return;
      }
      if (path.includes('/api/user/password')) {
        notifyOnce(notification, 'warning', t('passwordIncorrectTitle'), message);
        return;
      }
      // Permiso o regla de negocio con mensaje del backend (ya localizado por Accept-Language, p. ej.
      // error.vet.appointment.scheduleByReception); sin mensaje, el genérico.
      notifyOnce(notification, 'warning', t('forbiddenTitle'), hasBackendMessage(error) ? message : t('forbiddenMessage'));
      break;

    case 404:
      // Recurso inexistente: el backend trae el mensaje localizado del recurso (si no, uno genérico).
      notifyOnce(notification, 'info', t('notFoundTitle'), hasBackendMessage(error) ? message : t('notFoundMessage'));
      break;

    case 405:
      notifyOnce(notification, 'warning', t('methodNotAllowedTitle'), t('methodNotAllowedMessage'));
      break;

    case 409:
      // Versión optimista desactualizada (STALE_VERSION): mensaje claro de "recarga". La pantalla que
      // pueda, recarga el registro (`isStaleVersionError`); el resto de 409 (integridad, regla de
      // negocio) muestra el mensaje del backend.
      if (isStaleVersionError(error)) {
        notifyOnce(notification, 'warning', t('staleVersionTitle'), t('staleVersionMessage'));
        break;
      }
      notifyOnce(notification, 'warning', t('conflictTitle'), hasBackendMessage(error) ? message : t('conflictMessage'));
      break;

    case 413:
      notifyOnce(notification, 'warning', t('payloadTooLargeTitle'), t('payloadTooLargeMessage'));
      break;

    case 500: {
      // El 500 nunca muestra el detalle interno: mensaje genérico + id de correlación para soporte.
      const correlationId = correlationIdOf(error);
      notifyOnce(
        notification,
        'danger',
        t('serverErrorTitle'),
        correlationId
          ? transloco.translate('errors.http.serverErrorWithId', { id: correlationId })
          : t('serverErrorMessage')
      );
      break;
    }

    case 502:
    case 503:
    case 504:
      notifyOnce(notification, 'danger', t('unavailableTitle'), t('unavailableMessage'));
      break;

    default:
      // Otros 5xx tampoco exponen el mensaje interno.
      notifyOnce(notification, 'danger', t('genericTitle'), error.status >= 500 ? t('serverErrorMessage') : message);
      break;
  }
}